import type { DeepPartial, Nilable } from 'tsdef'

import { Unit } from '~/service'
import { DateLike } from './time'

// Firefox throws an error when using dateStyle and year/month/day together
// so test for support here.
const supportsDateStyle = (function () {
  try {
    return !!new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
      year: '2-digit',
    })
  } catch (e) {
    return false
  }
})()

/**
 * Takes a DateObject and returns a date string of that object with no timezone.
 * This mimics the Dates returned from the server.
 */
export function getDateStringNoTimeZoneFromDate(dateObject: Date) {
  //month is +1 becuase month count starts at 0.
  return `${dateObject.getFullYear()}-${
    dateObject.getMonth() + 1
  }-${dateObject.getDate()}`
}

/**
 * Pad a number with leading 0s if needed.
 */
const padTime = (t: string | number) => {
  if (typeof t === 'number' && isNaN(t)) return '00'
  return String(t).padStart(2, '0')
}

/**
 * Convert a GMT timezone offset like GMT-6 to an ISO timezone offset like
 * -06:00
 */
const gmtToISO = (timezone: string) => {
  const asString = timezone.split('GMT')[1]
  const abs = asString.replace('+', '').replace('-', '')
  const prefix = Number(asString) < 0 ? '-' : '+'
  return prefix + padTime(abs) + ':00'
}

/**
 * Convert a timezone-less ISO date (YYYY-MM-DD) into a date/time
 * relative to the given GMT timezone string.
 * @param date - The ISO date without timezone (ie. 'YYYY-MM-DD')
 * @param timezone - The GMT timezone offset (ex. 'GMT-7')
 */
export function addTimezoneToDate(
  date: DateLike,
  timezone = 'GMT+0',
  hours = 0,
  minutes = 0,
  seconds = 0
) {
  return new Date(
    date +
      `T${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}` +
      gmtToISO(timezone)
  ).toISOString()
}

/**
 * Format a date string for display.
 *
 * @param date The date string to parse and format.
 * @param locale This should only be passed during testing
 *   unless you have a really good reason to specify the locale.
 */
export function formatDateShort(
  date: DateLike,
  locale = navigator.language,
  dateStyle: Intl.DateTimeFormatOptions['dateStyle'] = 'short'
) {
  if (!date) {
    return ''
  }
  const fallbackOptions: Intl.DateTimeFormatOptions = {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
  }

  if (dateStyle === 'long') {
    fallbackOptions.year = 'numeric'
    fallbackOptions.weekday = 'short'
    fallbackOptions.month = 'long'
  }

  const options = supportsDateStyle
    ? { dateStyle, ...fallbackOptions }
    : fallbackOptions

  const formatter = new Intl.DateTimeFormat(locale, options)
  return formatter.format(new Date(date))
}

/**
 * Format a date string for display as time only.
 *
 * @param date The date string to parse and format.
 * @param locale This should only be passed during testing.
 * @param timeZone This should only be passed during testing.
 */
export function formatTime(
  date: DateLike = new Date(),
  locale = navigator.language,
  timeZone?: string
) {
  const options: Intl.DateTimeFormatOptions = {
    timeStyle: 'short',
  }
  if (timeZone) {
    options.timeZone = timeZone
  }
  const formatter = new Intl.DateTimeFormat(locale, options)
  return formatter.format(new Date(date))
}

/**
 * Format a time between starting and end times.
 */
export function formatTimeRange(
  accessStart: Nilable<DateLike>,
  accessEnd: Nilable<DateLike>
) {
  if (accessStart && accessEnd) {
    return `${formatTime(accessStart)} - ${formatTime(accessEnd)}`
  } else if (accessStart) {
    return formatTime(accessStart)
  } else if (accessEnd) {
    return `until ${formatTime(accessEnd)}`
  } else {
    // No Access
    return ''
  }
}

/**
 * Format a date string Ex. Tuesday May, 29th
 *
 * @param date The date string to parse and format.
 * @param locale This should only be passed during testing.
 * @param timeZone This should only be passed during testing.
 */
export function formatDate(
  date: DateLike,
  locale = navigator.language,
  timeZone?: string
) {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }
  if (timeZone) {
    options.timeZone = timeZone
  }
  let result = new Date(date).toLocaleDateString(locale, options)
  return result !== 'Invalid Date' ? result : undefined
}

/**
 * Format a date string Ex. Tuesday May, 29th 9.00AM
 *
 * @param date The date string to parse and format.
 * @param locale This should only be passed during testing.
 * @param timeZone This should only be passed during testing.
 */
export function formatDateWithTime(
  date: DateLike,
  locale = navigator.language,
  timeZone?: string
) {
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
  if (timeZone) {
    options.timeZone = timeZone
  }
  let result = new Date(date).toLocaleDateString(locale, options)
  return result !== 'Invalid Date' ? result : undefined
}

/**
 * Determines if a date is between two dates inclusively.
 *
 * @param date The date string to check.
 * @param minDate The start of the date range.
 * @param maxDate The end of the date range.
 */
export function isDateBetween(
  date: DateLike,
  minDate: DateLike,
  maxDate: DateLike
) {
  const time = new Date(date).getTime()
  const minTime = new Date(minDate).getTime()
  const maxTime = new Date(maxDate).getTime()
  if (minTime > maxTime) {
    console.warn('The minDate should be before the maxDate')
    return false
  }
  return minTime <= time && time <= maxTime
}

/**
 * Determines if the dates are equal.
 *
 * @param date - date to compare to now.
 */
export const isDateToday = (date: DateLike) => {
  const today = new Date()
  const dateToCompare = new Date(date)
  if (
    today.getDay() === dateToCompare.getDay() &&
    today.getDate() === dateToCompare.getDate() &&
    today.getFullYear() === dateToCompare.getFullYear()
  ) {
    return true
  } else {
    return false
  }
}

/**
 * Format a currency value for display.
 *
 * @param pennies - The value in pennies to parse into a currency.
 * @param currency - The currency to use (ex. USD, GBP).
 * @param locale - This should only be passed during testing
 *   unless you have a really good reason to specify the locale.
 */
export function formatCurrency(
  pennies: number,
  currency = 'USD',
  locale = navigator.language
) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(pennies / 100)
}

/**
 * Format a number for display in the user's locale (ex 1,000.00)
 */
export function formatNumber(value: number, locale = navigator.language) {
  const num = new Intl.NumberFormat(locale)
  return num.format(value)
}

/**
 * Format a number of beds into a string for display.
 */
export function formatBeds(beds: Nilable<number>) {
  return beds && beds > 0 ? `${beds} bd` : 'Studio'
}

/**
 * Format a number of baths into a string for display.
 */
export function formatBaths(baths: number | undefined) {
  return baths ? baths + ' ba' : ''
}

/**
 * Format a square footage into a string for display.
 */
export function formatFootage(feet: number | undefined) {
  return feet ? formatNumber(feet) + ' sqft' : ''
}

/**
 * Format a lease term in months.
 */
export function formatLeaseTerm(months: number) {
  const duration = months > 1 ? 'months' : 'month'
  return `${months} ${duration}`
}

/**
 * Whether the unit has a real building number ("N/A" means none).
 */
function hasBuildingNumber(unit: Nilable<DeepPartial<Unit>>) {
  return !!unit?.buildingNumber && unit.buildingNumber.toLowerCase() !== 'n/a'
}

/**
 * Given a unit object, render the text for its unit number badge:
 * "<buildingNumber>-<unitNumber>" (ex. "03-104"). The building is only
 * included when includeBuildingNumber is true and the unit has a building
 * number.
 */
export function formatUnitBadge(
  unit: Nilable<DeepPartial<Unit>>,
  includeBuildingNumber = false
) {
  const unitNumber = String(unit?.unitNumber ?? '')
  return unitNumber && includeBuildingNumber && hasBuildingNumber(unit)
    ? `${unit?.buildingNumber}-${unitNumber}`
    : unitNumber
}

/**
 * Given a unit object, render a readable name: the unit badge text (see
 * formatUnitBadge) followed by the floor plan name (ex. "03-104 Sagebrush").
 * If the unit has no floor plan, just the badge text is used.
 */
export function formatUnitName(
  unit: Nilable<DeepPartial<Unit>>,
  includeBuildingNumber = false
) {
  return [formatUnitBadge(unit, includeBuildingNumber), unit?.floorPlan?.name]
    .filter(Boolean)
    .join(' ')
}

/**
 * Format a lock device access code for display to the user.
 */
export function formatDoorCode(code: Nilable<string>, enter: Nilable<string>) {
  if (!code) return ''
  return `${code}${enter ?? ''}`
}

const DIGITS_ONLY_REGEX = /[^0-9]+/g

/**
 * Given a phone number and type (tel or sms), return an
 * href that can be used to call the number.
 */
export function formatPhoneNumberURL(phoneNumber: string, type = '') {
  type = type.toLowerCase()
  const phoneNumberType = type !== 'tel' && type !== 'sms' ? 'tel' : type
  const phoneDigits = String(phoneNumber).replace(DIGITS_ONLY_REGEX, '')
  return `${phoneNumberType}:${phoneDigits}`
}

/**
 * Ensure that a URL starts with a protocol.
 */
export function formatLink(url: string) {
  return url.indexOf('//') === -1 ? `http://${url}` : url
}

/**
 * Combine two class strings, removing duplicates and handling
 * empty, null or undefined strings.
 */
export function combineClasses(...classes: Nilable<string | boolean>[]) {
  // If we recieved 0 or 1 arguments, return immediately.
  if (arguments.length <= 1) return arguments[0]

  const list = classes.reduce((acc, arg) => {
    if (typeof arg === 'string') {
      const c = arg.trim().split(' ')
      if (c.length > 0) acc = acc.concat(c)
    }
    return acc
  }, [] as Nilable<string>[])

  if (list.length > 0) {
    return [...new Set(list)].join(' ').trim()
  }
}

/**
 * Escape RegExp special characters in a string.
 * This is useful for constructing dynamic RegExp
 * objects from arbitrary strings.
 * @param s - The string that may contain
 *   special characters.
 */
export function escapeRegexChars(s: string) {
  return s.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1')
}

/**
 * Trim the specified character from the begining and end of
 * the given string.
 * @param s - String to trim
 * @param char - Character to trim from strimg
 */
export function trimChar(s: string, char: string) {
  char = escapeRegexChars(char)
  const exp = new RegExp(`^${char}+|${char}+$`, 'g')
  return s.replace(exp, '')
}
